
// Import react elements
import React, { useEffect, useMemo, useState, useCallback } from "react";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";
import CreateButton from "global/components/CreateButton";
import { Grid, GridItem } from "global/components/Grid";
import { usePersistentFilters } from 'global/hooks/usePersistentFilters';
import { useToast } from "global/hooks/useToast";


// Import components
import CustomisableTable from 'global/components/CustomisableTable';
import CreateCheckboxFilters from 'global/components/CreateCheckboxFilters';
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import {SettingsEditor} from "./MMMSettings";

// Import utilities
import { stableStringify } from "global/utilities/CheckData";

// Import css stylesheet
import "./LinkMMMs.css";
import { CloseIcon } from "global/strich_icons/StrichIcons";


const LinkMMMs = ({
    materials, 
    setMaterials,
    methods, 
    setMethods,
    modes, 
    setModes,
    links, 
    setLinks,
    loadingLinks, 
    setLoadingLinks,
    handleRefetchMMMsAndLinks
}) => {

    // State for storing MMMs
    //const [materials, setMaterials] = useState([]);
    //const [methods, setMethods] = useState([]);
    //const [modes, setModes] = useState([]); 

    // State for storing links
    //const [links, setLinks] = useState([]); 

    // States for storing selected MMMs
    const [selectedMaterial, setSelectedMaterial] = useState();
    const [selectedMethod, setSelectedMethod] = useState();
    const [selectedMode, setSelectedMode] = useState();

    // Set state for selected link (in table)
    const [selectedLink, setSelectedLink] = useState();

    // State for storing selected Link row in links table
    const [selectedLinkRows, setSelectedLinkRows] = useState([]);

    // Set loading state - while api call is fetching links
    //const [loadingLinks, setLoadingLinks] = useState(true);

    // Set boolean state for displaying quick checkbox filters
    const [toggleTableQuickFilters, setToggleQuickFilters] = useState(true);

    // Define toast options
    const { toastError } = useToast();

    // Track user choices for conflict resolution per key
    const [conflictPrefs, setConflictPrefs] = useState({});



    // Custom hook for fetching and managing pod design and product data
    const {
        createLink,
        removeMMMLink,
        //fetchAndFormatCustomProductMMMLinks
    } = useCustomProductsData(); 



    // Export a normalizer that converts any settings shape into a plain object
    const NormalizeSettings = (raw) => {

      // If nullish return empty object
      if (raw == null) return {};

      // If already a plain object, shallow clone to avoid mutation
      if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };

      // If it's an array like [{a:1}, {b:2}] merge into { a:1, b:2 }
      if (Array.isArray(raw)) {

        // Reduce array into single object
        return raw.reduce((acc, item) => {

          // Only merge plain objects
          if (item && typeof item === "object" && !Array.isArray(item)) {

            // Merge singleton key/value into accumulator
            Object.assign(acc, item);

          }

          // Return accumulator
          return acc;

        }, {});
      }

      // Fallback to empty object if invalid
      return {};
    };



    // Function to merge settings from selected material, method and mode (gives conflict options with two settings with same key but different value)
    const MergeMMMSettings = ({
      
      // Provide the 3 selected MMMs (or null/undefined if not selected yet)
      material,
      
      method,
      
      mode,
      
      // Explicit precedence (earlier wins) - default is Mode > Method > Material 
      precedence = ["mode", "method", "material"],
      
      // Explicit conflict preferences keyed by setting key from source ("material" | "method" | "mode") - This is user-chosen per-key overrides (e.g., { speed: "method" })
      conflictPrefs = {}

    }) => {

      // Normalize each settings object to a plain object
      const materialObj = NormalizeSettings(material?.settings);
      
      const methodObj   = NormalizeSettings(method?.settings);
      
      const modeObj     = NormalizeSettings(mode?.settings);

      // Collect all unique keys across the three sources
      const allKeys = new Set([
        ...Object.keys(materialObj),
        ...Object.keys(methodObj),
        ...Object.keys(modeObj)
      ]);

      // Prepare an object for merged/effective settings that will be shown/edited
      const mergedSettings = {};

      // Prepare a conflicts descriptor so the UI can present toggles - Structure: { [key]: { values: { material?: any, method?: any, mode?: any }, differingSources: string[] } }
      const conflicts = {};

      // Iterate every key that exists in any source
      allKeys.forEach((key) => {

        // Gather values per source for this key
        const valMaterial = materialObj.hasOwnProperty(key) ? materialObj[key] : undefined;
        
        const valMethod   = methodObj.hasOwnProperty(key) ? methodObj[key] : undefined;
        
        const valMode     = modeObj.hasOwnProperty(key) ? modeObj[key] : undefined;

        // Create a compact dictionary of where the key appears and with what value
        const present = {};
        
        if (valMaterial !== undefined) present.material = valMaterial;
        
        if (valMethod   !== undefined) present.method   = valMethod;
        
        if (valMode     !== undefined) present.mode     = valMode;

        // Compute set of unique values for this key
        const distinctValues = Array.from(new Set(Object.values(present).map(String)));

        // If only one distinct value (or key only appears in one source), no conflict
        if (distinctValues.length <= 1) {

          // Choose the only present value (regardless of source)
          mergedSettings[key] = Object.values(present)[0];

          // No conflict entry needed
          return;
        }

        // If we get here, there is a conflict (same key with different values) - Record it so the UI can show a toggle/radio choice
        conflicts[key] = {
          values: present,
          differingSources: Object.keys(present)
        };

        // Determine which source wins for this key:

        // 1) If user has chosen a preference for this key, use that
        const preferredSource = conflictPrefs[key];

        // 2) Otherwise use precedence order (first source in the list that has a value)
        const winningSource =
          preferredSource && present.hasOwnProperty(preferredSource)
            ? preferredSource
            : precedence.find((src) => present.hasOwnProperty(src));

        // Assign the winning value into the merged settings
        mergedSettings[key] = present[winningSource];

      });

      // Return the merged object and conflicts
      return { mergedSettings, conflicts };
    };







    

    // Hook for using link settings
    const UseLinkSettings = ({
      
          // Arrays loaded from API
          materials,
      
          methods,
      
          modes,
      
          // Precedence as default - could offer to change this at some point
          precedence = ["mode", "method", "material"]

      }) => {

      // Track the selected ids (coming from 3 dropdowns)
      const [selectedMaterialId, setSelectedMaterialId] = useState("");
      
      const [selectedMethodId, setSelectedMethodId]     = useState("");
      
      const [selectedModeId, setSelectedModeId]         = useState("");

      // Utility to find an entity by id safely
      const findById = useCallback((arr, id) => {
        
        // Coerce to number when ids are numeric (otherwise compare as string)
        const target = Number(id);
        
        // Return found record or undefined
        return (arr || []).find((x) => Number(x?.id) === target);
      
      }, []);

      // Resolve the selected records
      const selectedMaterial = useMemo(
        () => findById(materials, selectedMaterialId),
        [materials, selectedMaterialId, findById]
      );

      const selectedMethod = useMemo(
        () => findById(methods, selectedMethodId),
        [methods, selectedMethodId, findById]
      );

      const selectedMode = useMemo(
        () => findById(modes, selectedModeId),
        [modes, selectedModeId, findById]
      );

      // Merge settings from the three sources whenever selection or preferences change
      const { mergedSettings, conflicts } = useMemo(

        () =>

          MergeMMMSettings({
            material: selectedMaterial,
            method: selectedMethod,
            mode: selectedMode,
            precedence,
            conflictPrefs
          }),

        [selectedMaterial, selectedMethod, selectedMode, precedence]

      );


      
      // Handlers your dropdowns can call
      const handleSelectMaterial = useCallback((id) => {
        
        // Update selected id
        setSelectedMaterialId(id);
        
        // Optionally: clear conflict choices if they no longer apply
        setConflictPrefs((prev) => {
          
          // No change in prefs structure here — we just keep them; the memoized merge will ignore invalid ones
          return { ...prev };
        
        });
      
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [materials, methods, modes, findById, selectedMethodId, selectedModeId]);

      const handleSelectMethod = useCallback((id) => {
        
        // Update selected id
        setSelectedMethodId(id);
        
        // Keep prefs as-is (same rationale as above)
        setConflictPrefs((prev) => ({ ...prev }));
      
      }, []);

      const handleSelectMode = useCallback((id) => {
        
        // Update selected id
        setSelectedModeId(id);
        
        // Keep prefs as-is
        setConflictPrefs((prev) => ({ ...prev }));
      
      }, []);


        // When a radio is clicked, update conflictPrefs (don’t rebuild if same choice)
      const setConflictChoice = useCallback((key, source) => {

        // Update only when value actually changes
        setConflictPrefs((prev) => (prev[key] === source ? prev : { ...prev, [key]: source }));

      }, []);


      // Reset all conflict choices (e.g., if the user clicks a "reset" button)
      const clearConflictChoices = useCallback(() => {
        
        // Clear all selected conflict preferences
        setConflictPrefs({});
      
      }, []);


      // Expose a clean API to the page/component
      return {

        // Selected ids and setters for your dropdowns
        selectedMaterialId,
        selectedMethodId,
        selectedModeId,
        setSelectedMaterialId: handleSelectMaterial,
        setSelectedMethodId: handleSelectMethod,
        setSelectedModeId: handleSelectMode,

        // Effective merged settings for the link (bind to your SettingsEditor)
        linkSettings: mergedSettings,

        // Conflicts descriptor for showing toggles
        conflicts,

        // Allow choosing a winner for a specific key
        setConflictChoice,

        // Reset choices if needed
        clearConflictChoices

      };

    };















    // Function to convert object to array
    const SettingsObjectToArray = (obj) => {

      // Guard: if it's already an array, shallow clone it
      if (Array.isArray(obj)) return [...obj];

      // If nullish, return an empty array
      if (obj == null) return [];

      // If it's not an object, return empty array (unexpected shape)
      if (typeof obj !== "object") return [];

      // For stable UI, we derive a deterministic key order (alphabetical)
      const keys = Object.keys(obj).sort((a, b) => a.localeCompare(b));

      // Map each key to a singleton object
      return keys.map((k) => ({ [k]: obj[k] }));

    };



    // Use the hook to manage merged link settings + conflicts
    const {
      selectedMaterialId,
      selectedMethodId,
      selectedModeId,
      setSelectedMaterialId,
      setSelectedMethodId,
      setSelectedModeId,
      linkSettings,
      conflicts,
      setConflictChoice
    } = UseLinkSettings({ materials, methods, modes });



    // Local editable copy for the SettingsEditor so the user can type/change - an ARRAY of singleton objects (e.g. [{ a:1 }, { b:2 }])
    const [editableSettings, setEditableSettings] = useState([]);



    // Record if there has been a change in conflict choice
    const conflictHash = stableStringify(conflictPrefs);



    // Keep editableSettings in sync when the merged linkSettings change
    useEffect(() => {

      // Convert the latest object to the editor’s array shape
      const nextArray = SettingsObjectToArray(linkSettings);

      setEditableSettings(nextArray);

      // Run when an MMM is selected or when a conflict choice is made
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMaterial, selectedMethod, selectedMode, conflictHash]);



    // Set the watchKey for the settingsEditor so it knows to update when different MMM is selected
    const watchKey = `${selectedMaterialId}|${selectedMethodId}|${selectedModeId}`;
    


    // Render editable settings when the user selects MMMs from the dropdown
    const RenderMMMSettings = () => {
      
      return (

        <div className="link-creator">
            
          {/* Conflict resolution UI (only shows keys that differ) */}
          {Object.keys(conflicts).length > 0 && (

            <div className="link-creator__conflicts">

              <h4 className="link-creator__conflicts-title">Conflicting settings</h4>

              {Object.entries(conflicts).map(([key, info]) => (

                <div className="link-creator__conflict" key={key}>

                  <div className="link-creator__conflict-key">{key}</div>

                  <div className="link-creator__conflict-choices">

                    {info.differingSources.map((src) => (

                      <label key={src} className="link-creator__choice">

                        <input
                          type="radio"
                          name={`conflict-${key}`}
                          onChange={() => setConflictChoice(key, src)}
                        />

                        <span className="link-creator__choice-label">

                          {src}: {String(info.values[src])}

                        </span>

                      </label>

                    ))}

                  </div>

                </div>

              ))}

            </div>

          )}
                  
          {/* Editor for the merged MMM link settings */}
          <SettingsEditor
            settingsArray={editableSettings}
            onSettingsChange={setEditableSettings}
            disabled={false}
            watchKey={watchKey}
          />

        </div>
      );

    };



// Function to clean out any empty or half done settings, trim spaces, and return a single flat object fo rthe api like { key: "value", test: "test" }
const CleanSettingsArray = (settingsArray) => {

  // Guard: normalize non-arrays to an empty array
  const arr = Array.isArray(settingsArray) ? settingsArray : [];

  // First, filter out any rows with empty key or empty value (after trimming)
  const filtered = arr.filter((obj) => {

    // Get the single key from the singleton object
    const keyRaw = Object.keys(obj || {})[0];

    // Normalize/trim the key
    const key = String(keyRaw ?? "").trim();

    // Get the value for that key
    const valRaw = key ? obj[keyRaw] : undefined;

    // Normalize/trim the value (treat null/undefined as empty)
    const val = valRaw == null ? "" : String(valRaw).trim();

    // Keep only rows that have BOTH a non-empty key and a non-empty value
    return key !== "" && val !== "";

  });

  // Then, reduce the cleaned singletons into one flat object
  const result = filtered.reduce((acc, obj) => {

    // Extract the first (and only) key from the singleton object
    const kRaw = Object.keys(obj)[0];

    // Trim the key
    const k = String(kRaw).trim();

    // Get the original value
    const v0 = obj[kRaw];

    // Trim the value if it's a string; keep other types as-is
    const vTrimmed = typeof v0 === "string" ? v0.trim() : v0;

    // Assign into the accumulator (later entries with same key overwrite earlier ones)
    acc[k] = vTrimmed;

    // Return the accumulator for the next iteration
    return acc;

  }, {});

  // Return the final flat object, e.g. { colour: "red", output_ppi: "150", ink: "test" }
  return result;

};





    // Function to create a link between material, method and mode with settings
    const handleCreateLink = async (materialID, methodID, modeID, settings) => {
      
        // Create api payload of settings - Clean out empty settings and trim before creating new link
        const payload = CleanSettingsArray(settings);

        // Make the call
        const response = await createLink(materialID, methodID, modeID, payload);

        // Check if the response was successful
        if (response.success) {

            // Clear the selected material, method and mode
            setSelectedMaterial();
            setSelectedMethod();
            setSelectedMode();
            setSelectedMaterialId();
            setSelectedMethodId();
            setSelectedMethodId();
          
            // Refetch MMMs and Links
            handleRefetchMMMsAndLinks(setMaterials, setMethods, setModes, setLinks, setLoadingLinks);

        } else {

            // Toast fail message
            toastError({ title: "Failed", message: "Could not add link: " + response.error_message, autoCloseMs: 6000 });

        }

    };



    // ******** Links Table  ********

    // Columns for the links table
    const initialTableColumns = [
        // Select tick box at left of table rows - if we want to multi select orders and action them
        //{ key: 'select', label: '', resizable: false, minWidth: 40, maxWidth: 40, draggable: false, textFilter: false },
        { key: 'view', label: '', resizable: false, minWidth: 50, maxWidth: 50, draggable: false, textFilter: false },
        { key: 'material_name', label: 'Material', resizable: true, minWidth: 150, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'method_name', label: 'Method', resizable: true, minWidth: 150, maxWidth: 500, draggable: false, textFilter: true },
        { key: 'mode_name', label: 'Mode', resizable: true, minWidth: 150, maxWidth: 500, draggable: false, textFilter: true },
    ];


    // Set filters state - needs passing to filters component and table component
    const [filters, setFilters] = useState({});


    // Filter keys we want for the table of links
    const filterKeys = ['material_name', 'method_name', 'mode_name'];


    // This object maps internal filter keys to user-friendly labels
    const filterLabels = {
        material_name: 'Material',
        method_name: 'Method',
        mode_name: 'Mode',
    };


    // useMemo to extract unique values from links
    const uniqueFilterOptions = useMemo(() => {

        // Create an object to hold options for each filter type
        const options = {};

        // Iterate through all rows of links
        links.forEach((row) => {

            ['material_name', 'method_name', 'mode_name'].forEach((key) => {

                // Initialize a Set for each key if it doesn't exist
                if (!options[key]) options[key] = new Set();

                // Add the value to the set to avoid duplicates
                if (row[key] !== undefined) options[key].add(row[key]);

            });

        });

        // Convert the sets into sorted arrays
        return Object.fromEntries(

            Object.entries(options).map(([key, set]) => [key, Array.from(set).sort()])

        );

        // Dependencies that trigger a new instance of this function
    }, [links]);


    // Extract form-related state and functions from the `usePersistentFilters` custom hook  (for orders table filters)
    const {
        selectedFilters,
        toggleFilterValue,
        //toggleSelectAllFilters,
        //isAllSelected,
        clearFilterGroup,
    } = usePersistentFilters("MMMLinksTable", filterKeys);
          


    // useEffect to update table when filters are changed
    useEffect(() => {

        // Whenever selectedFilters changes, apply them to the table filters
        setFilters((prev) => ({
            ...prev,
            ...selectedFilters,
        }));

    }, [selectedFilters]);



    // Check if any checkbox filters have been selected - so we can change the collapse/expand icon colour
    const anyFiltersSelected = Object.values(selectedFilters).some((selectedFilter) => selectedFilter.length > 0);



    // Function for click on a link row in the table (view link details)
    const handleSelectLink = (linkIDs) => {

      // Check if it's an array of ids, if so take the first one, else use it as is
      const linkID = Array.isArray(linkIDs) ? linkIDs[0] : linkIDs;

      // Find the matching link in links array with same id
      const linkDetails = links.find((link) => link.id === linkID);

      // Update selected link state
      setSelectedLink(linkDetails);

      // Open a pop up with link details in it
      setIsLinkDetailsPopUpOpen(true);

    };



    // Function to handle delete link
    const handleDeleteLink = (linkIDs) => {

        //console.log("Deleted Link ID: ", linkIDs);

        // Display a confirmation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this link/these links?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (isConfirmed) {

                // Return early if they cancel
                if (!isConfirmed) return;

                // Track whether at least one deletion failed
                let hadFailure = false;

                // Loop through each selected link id SEQUENTIALLY
                for (const linkID of linkIDs) {

                // Find the matching link in links array with same id
                const linkDetails = links.find((link) => link.id === linkID);

                // If we can't find details, flag a failure and continue
                if (!linkDetails) {

                    // Show an error for debugging/user feedback
                    toastError({ title: "Failed", message: "Could not locate link details for id: " + linkID, autoCloseMs: 6000 });

                    // Flip the failure flag
                    hadFailure = true;

                    // Move to the next id
                    continue;

                }

                try {

                    // Make the call to delete it and WAIT for it to finish
                    const response = await removeMMMLink(linkDetails);

                    // If the API reports failure, show a message and mark as failure
                    if (!response?.success) {

                    // Show a failure toast
                    toastError({ title: "Failed", message: "Could not delete link: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });

                    // Flip the failure flag
                    hadFailure = true;
                    }

                } catch (err) {

                    // Catch network/runtime errors
                    console.error("remove error: ", err);

                    // Show a failure toast/message
                    toastError({ title: "Failed", message: "Unexpected error while deleting link id: " + linkID, autoCloseMs: 6000 });

                    // Flip the failure flag
                    hadFailure = true;

                }

                }

                // After processing them all, refetch once to refresh the UI
                // Refetch MMMs and Links
                handleRefetchMMMsAndLinks(setMaterials, setMethods, setModes, setLinks, setLoadingLinks);

                // Optionally, you can notify if some deletions failed while others succeeded
                if (hadFailure) {

                // Show a warning that some items may remain
                toastError({ title: "Partial Success", message: "Some links could not be deleted. The links table has been refreshed.", autoCloseMs: 6000 });

                }

            }

        );

    };



    // Create menu options array - used for dropdown above table, per table row and within pop up?
    const linksActionsConfig = [
        {
            id: "view", // Unique id
            label: 'View Details', // Title label
            clickFunction: handleSelectLink, // Click function used for table row button
            icon: '✏️', // Optional icon
            disabled: false, // Disabled state
            singleItemAction: true, // Is this action just for single item
        },
        {
            id: "delete",
            label: 'Delete',
            clickFunction: handleDeleteLink,
            icon: '🗑️',
            disabled: false,
            color: 'red',
        },

    ];

    const [isLinkDetailsPopUpOpen, setIsLinkDetailsPopUpOpen] = useState(false); 
    

    // Create a memoized, descending-by-id copy of links - to send to the link table so the most recently created is at the top of the table
    const linksByIdDesc = useMemo(() => {

      // Guard: ensure we always work with an array
      const arr = Array.isArray(links) ? links : [];

      // Return a new array (don’t mutate state) sorted by numeric id descending
      return [...arr].sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0));

      // Recompute whenever links reference changes
    }, [links]);

    

 




/* ----------------- Settings Editor ---------------- */

    // Render list of settings (key and value)
    const RenderSettingsPopUp = (settings) => {

      const settingsArray = SettingsObjectToArray(settings);

        // Map each setting to a labeled input
        return settingsArray.map((obj) => {

            // Extract the single key from the object (or empty string if none)
            const key = Object.keys(obj || {})[0] ?? "";

            // Extract the value by that key (or empty string if missing)
            const value = obj?.[key] ?? "";

            // Return a normalized row
            return (

                // Parent container with the list class
                <div className="settings-list" key={key}>

                  {/* One item per setting */}
                  <div className="settings-list__item" key={key}>
                    <span className="settings-list__key">{key}</span>
                    <span className="settings-list__value">{String(value)}</span>
                  </div>

                </div>

            );

        })
        
    };




/* ----------------- Link Details Pop Up ---------------- */

    // Pop up with link details - including M, M + M and settings
    const LinkDetailsPopup = ({ isOpen, onClose, linkDetails }) => {

      // If isOpen is false return early (don't show the pop up)
      if (!isOpen) return null;

      // Else show the pop up
      return (

        <>

            <div className="popup-overlay">

                <div className="popup-container popup-container--small">

                    {/* This is the modal header */}
                    <div className="popup__header">

                        {/* This is the title area */}
                        <div className="popup__headerLeft">

                            {/* This is the parent title */}
                            <h2 className="popup__title">

                                Link Details

                            </h2>

                            {/* This is the parent subtitle */}
                            <p className="popup__subtitle">

                            </p>

                        </div>

                        {/* This is the header actions */}
                        <div className="popup__headerRight">

                            {/* This is the close button */}
                            <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                                <CloseIcon />
                            </button>

                        </div>

                    </div>

                    <div className="popup-content popup-content__body">

                            <Grid className={"popup_grid-container"} >

                                <div>

                                    <br/>

                                    <div className="link-details_link-trio">

                                        <div className="horizontal-align-content-center">
                                          
                                          <div className="link-details_link-trio_MMM">Material <br/> <h2>{linkDetails.material_name}</h2></div>
                                          +
                                          <div className="link-details_link-trio_MMM">Method <br/> <h2>{linkDetails.method_name}</h2></div>
                                          +
                                          <div className="link-details_link-trio_MMM">Mode <br/> <h2>{linkDetails.mode_name}</h2></div>  

                                        </div>

                                    </div>

                                    <br/>

                                    <div className="link-details_settings">

                                        <br/>

                                        <h3>Settings:</h3>

                                        <br />

                                        {linkDetails.settings ?

                                            RenderSettingsPopUp(linkDetails.settings)

                                            :

                                            "No settings have been assigned to this link."
                                        }

                                    </div>

                                    <br />
                                    
                                    <div>

                                        <p>*If you need to edit or add settings, simply re-create the link with the required settings.</p>

                                    </div>

                                </div>

                            </Grid>

                    </div>

                </div>

            </div>

        </>

      )

  };






    return (

        <>

            <Grid>

                <GridItem>
                  
                  <div>

                    <h3>Create a Link</h3>

                    <br/>

                    <div className="MMM-Grid">
                    
                        <div>
                            <h3>Material</h3>
                            <select 
                                className="MMM-Grid_select" 
                                value={selectedMaterial ?? ""}
                                onChange={(e) => {
                                    setSelectedMaterial(e.target.value || undefined);
                                    setSelectedMaterialId(e.target.value);
                                }}
                                >
                                <option value="" disabled>Select a material...</option>
                                {materials.map((m) => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>    
                        </div>

                        <div>
                            <h3>Method</h3>
                            <select className="MMM-Grid_select" 
                              value={selectedMethod?? ""} 
                              onChange={(e) => {
                                setSelectedMethod(e.target.value || undefined);
                                setSelectedMethodId(e.target.value);
                                }}
                              >
                                <option value="" disabled>Select a method...</option>
                                {methods.map((m) => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>     
                        </div>

                        <div>
                            <h3>Mode</h3>
                            <select className="MMM-Grid_select" 
                              value={selectedMode?? ""} 
                              onChange={(e) => {setSelectedMode(e.target.value || undefined);
                                setSelectedModeId(e.target.value);

                              }}
                            >
                                <option value="" disabled>Select a mode...</option>
                                {modes.map((m) => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>     
                        </div>
                    
                        <div>

                            <br/>

                            <CreateButton title="Create Link" size="m" className="MMM-Grid_select" onClick={() => handleCreateLink(selectedMaterial, selectedMethod, selectedMode, editableSettings)} disabled={!selectedMaterial || !selectedMethod || !selectedMode} />

                        </div>

                    </div>
            
                    

                        {selectedMaterial || selectedMethod || selectedMode ?

                          <>

                            <br/>

                            <div className="MMM-Grid_settings">
                            
                              <h4>Link Settings:</h4>

                              {RenderMMMSettings()}

                            </div>

                          </>

                            : 

                            ""

                        }
                    

                  </div>
                            
                </GridItem>

            </Grid>

            <Grid>
                
                <GridItem>

                    <div>

                      <h3 className="MMM-Grid_table-title">Links ( {loadingLinks ? "" : links.length} )</h3>
                      <br/>

                    </div>

                    <div style={{ overflowX: 'auto', minWidth: '100%' }}>

                        <div className="quick_filters_and_table">

                            <div className="collapsable_quick_filters">

                                <div className="collapse_quick_filters_button_banner" onClick={() => { setToggleQuickFilters((prev) => !prev) }}>

                                    <button
                                        title={toggleTableQuickFilters ? "Hide Quick Filters" : "Show Quick Filters"}
                                        className={"collapse_quick_filters_button " + (anyFiltersSelected ? "highlight_quick_filters_icon" : "") }
                                    >

                                        {toggleTableQuickFilters ? "❮" : "🝖"}
                                    
                                    </button> 

                                </div>
                
                                {toggleTableQuickFilters ?

                                    <div className="filter_groups">

                                        <CreateCheckboxFilters
                                        uniqueFilterOptions={uniqueFilterOptions}
                                        selectedFilters={selectedFilters}
                                        filterLabels={filterLabels}
                                        clearFilterGroup={clearFilterGroup}
                                        toggleFilterValue={toggleFilterValue}
                                        setSelectedRows={setSelectedLinkRows}
                                    />

                                    </div>

                                    : ""

                                }

                            </div>

                            <div>

                                <CustomisableTable 
                                    tableData={linksByIdDesc} 
                                    initialTableColumns={initialTableColumns}   
                                    handleRowSingleClick={handleSelectLink}
                                    selectedRows={selectedLinkRows}
                                    setSelectedRows={setSelectedLinkRows}
                                    noDataAvaiableMessage={"No links have been created yet."}
                                    loadingData={loadingLinks}
                                    filters={filters}
                                    setFilters={setFilters}
                                    menuOptions={linksActionsConfig}
                                />

                            </div>

                        </div>

                    </div>

                </GridItem>    
                
            </Grid>   
            

            {isLinkDetailsPopUpOpen && 

                selectedLink && 
                
                <LinkDetailsPopup 
                    isOpen={isLinkDetailsPopUpOpen} 
                    onClose={() => {
                        setIsLinkDetailsPopUpOpen(false);
                        //setSelectedLink();
                    }} 
                    linkDetails={selectedLink}
                />
                
            }                 

        </>

    )

};

export default LinkMMMs;